import React from 'react'
import { Box, Typography } from '@mui/material'
import { ChevronLeftIcon, ChevronRightIcon } from '@heroicons/react/24/outline'

import Button from '../../../../interaxDesignSystemComponents/Button'
import { INTERAX_COLORS } from '../../../../constants/colors'

interface OnboardingStepNavProps {
  isFirstStep: boolean
  isLastStep: boolean
  backDisabled?: boolean
  /** Disables both Next on intermediate steps and Activate on the final step. */
  primaryDisabled?: boolean
  primaryLoading?: boolean
  /** Label for the trailing primary action — caller picks "Continue" or "Activate". */
  primaryLabel: string
  backLabel: string
  onBack: () => void
  onPrimary: () => void
}

/**
 * Bottom action bar — back (left) + primary action (right). Visually matches
 * `FlowBottomComponent` but omits all draft/template controls. The primary
 * action is the wizard's sole forward affordance; the parent decides whether
 * it's "Continue" (intermediate step) or "Activate organization" (final step).
 */
export const OnboardingStepNav: React.FC<OnboardingStepNavProps> = ({
  isFirstStep,
  isLastStep,
  backDisabled,
  primaryDisabled,
  primaryLoading,
  primaryLabel,
  backLabel,
  onBack,
  onPrimary
}) => {
  const iconInlineSize = { width: 16, height: 16 } as const
  const effectiveBackDisabled = backDisabled || isFirstStep

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: { xs: 2, sm: 2, md: 2 },
        pt: { xs: 2, sm: 2, md: 2 },
        borderTop: `1px solid ${INTERAX_COLORS.thunderGrey}`
      }}
    >
      <Box
        component="button"
        type="button"
        onClick={onBack}
        disabled={effectiveBackDisabled}
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: { xs: 1, sm: 1, md: 1 },
          m: 0,
          p: 0,
          border: 'none',
          background: 'none',
          cursor: effectiveBackDisabled ? 'not-allowed' : 'pointer',
          opacity: effectiveBackDisabled ? 0.5 : 1,
          color: INTERAX_COLORS.white,
          font: 'inherit',
          textAlign: 'left',
          transition: 'color 0.2s ease',
          '&:hover': {
            color: effectiveBackDisabled ? INTERAX_COLORS.white : INTERAX_COLORS.sun
          }
        }}
      >
        <ChevronLeftIcon aria-hidden="true" style={iconInlineSize} />
        <Typography component="span" variant="body2" sx={{ color: 'inherit' }}>
          {backLabel}
        </Typography>
      </Box>

      <Button
        variant="primary"
        onClick={onPrimary}
        disabled={Boolean(primaryDisabled) || Boolean(primaryLoading)}
        className="flex items-center gap-2"
      >
        {primaryLabel}
        {!isLastStep && <ChevronRightIcon className="w-4 h-4" aria-hidden="true" />}
      </Button>
    </Box>
  )
}
